import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Navbar } from './components/layout/Navbar';
import { Footer } from './components/layout/Footer';

// Public Pages
import Home from './pages/Home';
import Courses from './pages/Courses';
import CourseDetail from './pages/CourseDetail';
import About from './pages/About';
import Contact from './pages/Contact';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import AuthCallback from './pages/Auth/AuthCallback';
import Enrollment from './pages/Enrollment';
import Checkout from './pages/Checkout';

// Legal Pages
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import RefundPolicy from './pages/RefundPolicy';

// Mentor Registration (Public)
import MentorRegistration from './pages/Mentor/Registration';

// Admission Form (Public)
import Apply from './pages/Apply';

// Dashboard Pages (Protected - No navbar/footer)
import StudentDashboard from './pages/Student/Dashboard';
import CoursePlayer from './pages/Student/CoursePlayer';
import AITutor from './pages/Student/AITutor';
import Chat from './pages/Student/Chat';
import MockTests from './pages/Student/MockTests';
import AdminDashboard from './pages/Admin/Dashboard';
import MentorDashboard from './pages/Mentor/Dashboard';
import ContentDashboard from './pages/Content/Dashboard';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    // Redirect based on role
    if (user?.role === 'admin') return <Navigate to="/admin" replace />;
    if (user?.role === 'teacher') return <Navigate to="/mentor" replace />;
    if (user?.role === 'content_executive') return <Navigate to="/content" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Dashboard Layout - No navbar/footer, focused workspace
const DashboardLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-background">
      {children}
      <Toaster position="top-right" richColors closeButton />
    </div>
  );
};

// Public Layout - With navbar/footer
const PublicLayout = ({ children }) => {
  return (
    <>
      <Navbar />
      <main className="min-h-screen">{children}</main>
      <Footer />
      <Toaster position="top-right" richColors closeButton />
    </>
  );
};

// App Router Component
function AppRouter() {
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();

  // Check for OAuth callback (session_id in URL fragment)
  // Only handle via hash if we're NOT already on the /auth/callback route
  // to prevent double rendering
  if (location.hash?.includes('session_id=') && location.pathname !== '/auth/callback') {
    return <AuthCallback />;
  }

  // Dashboard routes - no navbar/footer
  // Note: /mentor/register is a PUBLIC route, so exclude it from dashboard detection
  const isDashboardRoute = [
    '/dashboard',
    '/admin',
    '/content',
    '/student'
  ].some(path => location.pathname.startsWith(path)) || 
  (location.pathname.startsWith('/mentor') && location.pathname !== '/mentor/register');

  // Render dashboard routes
  if (isDashboardRoute) {
    return (
      <DashboardLayout>
        <Routes>
          {/* Student Dashboard Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute allowedRoles={['student']}>
              <StudentDashboard />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/course/:courseId" element={
            <ProtectedRoute allowedRoles={['student', 'admin', 'teacher']}>
              <CoursePlayer />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/course/:courseId/module/:moduleSkill" element={
            <ProtectedRoute allowedRoles={['student', 'admin', 'teacher']}>
              <CoursePlayer />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/ai-tutor" element={
            <ProtectedRoute allowedRoles={['student', 'admin', 'teacher']}>
              <AITutor />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/chat" element={
            <ProtectedRoute allowedRoles={['student', 'admin', 'teacher']}>
              <Chat />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/mock-tests" element={
            <ProtectedRoute allowedRoles={['student', 'admin', 'teacher']}>
              <MockTests />
            </ProtectedRoute>
          } />
          
          {/* Admin Dashboard Routes */}
          <Route path="/admin" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/*" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          
          {/* Mentor Dashboard Routes */}
          <Route path="/mentor" element={
            <ProtectedRoute allowedRoles={['teacher', 'admin']}>
              <MentorDashboard />
            </ProtectedRoute>
          } />
          <Route path="/mentor/*" element={
            <ProtectedRoute allowedRoles={['teacher', 'admin']}>
              <MentorDashboard />
            </ProtectedRoute>
          } />

          {/* Content Executive Dashboard */}
          <Route path="/content" element={
            <ProtectedRoute allowedRoles={['content_executive', 'admin']}>
              <ContentDashboard />
            </ProtectedRoute>
          } />
          <Route path="/content/*" element={
            <ProtectedRoute allowedRoles={['content_executive', 'admin']}>
              <ContentDashboard />
            </ProtectedRoute>
          } />

          {/* Legacy routes redirect */}
          <Route path="/student/dashboard" element={<Navigate to="/dashboard" replace />} />
          <Route path="/student/*" element={<Navigate to="/dashboard" replace />} />
          <Route path="/teacher/dashboard" element={<Navigate to="/mentor" replace />} />
          <Route path="/teacher/*" element={<Navigate to="/mentor" replace />} />
          
          {/* Catch all dashboard */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </DashboardLayout>
    );
  }

  // If user is authenticated and on a public page (except courses which can be viewed), redirect to dashboard
  if (isAuthenticated && ['/', '/login', '/register'].includes(location.pathname)) {
    if (user?.role === 'admin') return <Navigate to="/admin" replace />;
    if (user?.role === 'teacher') return <Navigate to="/mentor" replace />;
    if (user?.role === 'content_executive') return <Navigate to="/content" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  // Render public routes with navbar/footer
  return (
    <PublicLayout>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Home />} />
        <Route path="/courses" element={<Courses />} />
        <Route path="/courses/:courseId" element={<CourseDetail />} />
        <Route path="/enroll/:courseId" element={<Enrollment />} />
        <Route path="/checkout/:courseId" element={<Checkout />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/faculty" element={<About />} />
        <Route path="/admissions" element={<About />} />
        
        {/* Auth Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/apply" element={<Apply />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        
        {/* Admin Login Route */}
        <Route path="/admin/login" element={<Login />} />
        
        {/* Mentor Registration (Public) */}
        <Route path="/mentor/register" element={<MentorRegistration />} />
        
        {/* Legal Pages */}
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        
        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </PublicLayout>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
